import { Injectable } from '@angular/core';

import { OfferSource } from '@cds';
import { Fixture, OrderType } from '@cds/betting-offer';
import { SplitFixture } from '@cds/betting-offer/domain-specific';
import { BetBuilderFixture } from '@cds/betting-offer/domain-specific/bet-builder';
import { DetailedFixtureFactory, FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    EnhancedFixtureViewGroupingConfiguration,
    EventModel,
    EventOption,
    EventOptionGroup,
    EventOptionGroupType,
    EventOptionGrouping,
    ExtendedDisplayType,
    Tv2MarketParameters,
} from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { TopEventMarketFixtureSource, TopEventsMarketParameters } from '@frontend/sports/types/components/top-events';
import { cloneDeep, entries, indexOf, sortBy, uniqBy } from 'lodash-es';

import { EventDetailsService } from '../event-details-common/event-details.service';
import { EventNameMapperService } from '../event-details-common/event-name-mapper.service';
import { EventDataForOptionPanel, PodEventModelRequest, TopEventsPodData, TopEventsSourceFixture } from './top-events.model';

@Injectable({ providedIn: 'root' })
export class TopEventsMappingService {
    private readonly tv2MarketParamsDict = {
        marketType: Tv2MarketParameters.MarketType,
        happening: Tv2MarketParameters.Happening,
        period: Tv2MarketParameters.Period,
    };

    constructor(
        private eventDetailsService: EventDetailsService,
        private detailedFixtureFactory: DetailedFixtureFactory,
        private fixtureFactory: FixtureFactory,
        private eventNameMapper: EventNameMapperService,
        public sitecore: Sitecore,
    ) {}

    createFixture(
        isDetailed: boolean,
        fixture: Fixture,
        splitFixtures?: SplitFixture[],
        detailedGrouping?: EnhancedFixtureViewGroupingConfiguration,
        betBuilderFixture?: BetBuilderFixture,
    ): EventModel | undefined {
        if (!isDetailed) {
            return this.fixtureFactory.create(fixture);
        }

        if (!detailedGrouping) {
            return undefined;
        }

        const grouping = betBuilderFixture
            ? this.eventDetailsService.addBetbuilderToGrouping(cloneDeep(detailedGrouping), betBuilderFixture)
            : detailedGrouping;

        return this.detailedFixtureFactory.create({
            fixture,
            splitFixtures: splitFixtures ?? [],
            grouping,
            precreated: betBuilderFixture,
        });
    }

    getPodEventData(request: PodEventModelRequest): EventDataForOptionPanel {
        let preparedOptionGoups: EventOptionGroup[] = [];
        request.eventData.name = this.eventNameMapper.map(request.eventData, true);

        if (request.eventData.optionGroups.length) {
            if (request.blackListMarketGroupIds?.length) {
                request.eventData.optionGroups = request.eventData.optionGroups.filter(
                    (x) => !request.blackListMarketGroupIds!.includes(x.detailedGrouping.marketGroupItemId),
                );
            }
            // remove duplicate markets that appear in multiple optionGroups
            if (!request.allowDuplicates) {
                request.eventData.optionGroups = uniqBy(request.eventData.optionGroups, (og) => og.id);
            }

            if (request.isDetailed) {
                // will order them by marketOrder
                preparedOptionGoups = this.eventDetailsService.prepareEventOptionGroups({ event: request.eventData });

                // revert ordering to sitecore betpicker selection order
                if (request.orderAsConfigured !== false) {
                    preparedOptionGoups = sortBy(preparedOptionGoups, (prepared) =>
                        indexOf([...request.sourceFixtures[request.eventData.id].optionGroupIds], prepared.originalId ?? prepared.id),
                    );
                }
            } else {
                preparedOptionGoups = request.eventData.optionGroups;
            }

            if (request.selectionTextOverride) {
                preparedOptionGoups.forEach((og) => og.options.forEach((o) => (o.name = request.selectionTextOverride![o.id] || o.name)));
            }
        }

        return {
            eventModel: request.eventData,
            preparedOptionGoups,
        } as EventDataForOptionPanel;
    }

    getPodEventDataBetBuilderOptions(request: PodEventModelRequest, optionIds: number[] = []): EventDataForOptionPanel {
        if (optionIds.length === 0) {
            return {
                eventModel: request.eventData,
                preparedOptionGoups: [],
            } as EventDataForOptionPanel;
        }

        const options: EventOption[] = [];

        optionIds.forEach((optionId) => {
            request.eventData.optionGroups.forEach((og) => {
                const option = og.options.find((o) => o.id === optionId);
                if (option) {
                    options.push(option);
                }
            });
        });

        const eventOptionGrouping: EventOptionGrouping = {
            displayType: ExtendedDisplayType.BetBuilder,
            marketTabId: 0,
            name: this.sitecore.betBuilder.messages?.PrecreatedBuildABetTitle,
            marketHelpPath: this.sitecore.betBuilder.messages?.PrecreatedBuildABetTitleHelpText,
            marketGroupId: '',
            marketGroupItemId: '',
            marketSet: 0,
            marketOrder: 0,
            orderType: OrderType.None,
        };

        const eventOptionGroup: EventOptionGroup = {
            detailedGrouping: eventOptionGrouping,
            comboPrevention: request.eventData.optionGroups[0].comboPrevention,
            hidden: false,
            id: '',
            originalId: '',
            name: '',
            position: 0,
            isEachWay: false,
            isStartingPriceAvailable: false,
            isWinPriceAvailable: false,
            online: true,
            visible: true,
            templateId: '',
            options,
            winning: false,
            type: EventOptionGroupType.Regular,
            isBetBuilder: false,
            hasBetBuilderMarkets: false,
            hasSubGroups() {
                return false;
            },
            hasGroup() {
                return false;
            },
            hasAnyGroup() {
                return false;
            },
            getSubGroups() {
                return [];
            },
        };

        request.eventData.optionGroups = [eventOptionGroup];

        return {
            eventModel: request.eventData,
            preparedOptionGoups: request.eventData.optionGroups,
        } as EventDataForOptionPanel;
    }

    filterEvent(event: EventModel, source: TopEventsSourceFixture): EventModel {
        event.optionGroups = event.optionGroups.filter((og) => source.optionGroupIds.has(og.id));
        event.optionGroups.forEach((og) => (og.options = og.options.filter((o) => source.optionIds.has(o.id))));

        return event;
    }

    private getFixtureParticipantIdFromParticipantId(fixture: Fixture, participantId: number | undefined): number | undefined {
        return fixture.participants.find((p) => p.id === participantId)?.participantId;
    }

    private filterOptionGroupsByTemplateIds(event: EventModel, templateIds: number[] | undefined): EventOptionGroup[] {
        if (!templateIds) return [];
        return event.hybridFixtureData
            ? event.optionGroups.filter((og) => {
                  const templateIdParam = og.parameters?.find((p) => p.key === Tv2MarketParameters.TemplateId)?.value;
                  if (!templateIdParam && !og.parameters) {
                      return templateIds.includes(Number(og.templateId));
                  }

                  return templateIdParam !== undefined && templateIds.includes(parseInt(templateIdParam));
              })
            : event.optionGroups.filter((og) => templateIds.includes(Number(og.templateId)));
    }

    private filterOptionGroupsByPlayerIds(event: EventModel, playerIds: number[] | undefined, pod: TopEventsPodData): void {
        if (playerIds !== undefined && event.offerSource === OfferSource.V2) {
            event.optionGroups = event.optionGroups.filter((og) => {
                const fixture = pod.fixtures.find((f) => f.id === event.id);

                return (
                    fixture?.optionMarkets
                        .find((om) => om.id.toString() === og.id)
                        ?.options.some((option) => {
                            const fixtureParticipantId = this.getFixtureParticipantIdFromParticipantId(
                                fixture,
                                option.parameters?.fixtureParticipant,
                            );

                            return fixtureParticipantId ? playerIds.includes(fixtureParticipantId) : false;
                        }) ?? false
                );
            });
        }
    }

    private filterOptionGroupsByHybridPlayerIds(event: EventModel, playerIds: number[] | undefined, pod: TopEventsPodData): void {
        if (event.offerSource === OfferSource.V1 && event.hybridFixtureData?.fixtureId !== undefined && playerIds !== undefined) {
            event.optionGroups = event.optionGroups.filter((og) => {
                const fixtureParticipant = og.originalParameters?.find((p) => p.key === Tv2MarketParameters.FixtureParticipant)?.value;
                const fixtureParticipantList = og.originalParameters
                    ?.find((p) => p.key === Tv2MarketParameters.FixtureParticipantList)
                    ?.value.split(',')
                    .map(Number);

                const fixture = pod.fixtures.find((f) => f.id === event.id);

                const optionFixtureParticipantList =
                    fixture?.optionMarkets
                        ?.find((om) => om.id.toString() === og.id)
                        ?.options?.map((option) => {
                            const fixtureParticipantId = this.getFixtureParticipantIdFromParticipantId(
                                fixture,
                                option.parameters?.fixtureParticipant,
                            );

                            return fixtureParticipantId;
                        }) ?? undefined;

                if (fixtureParticipant) {
                    return playerIds.includes(parseInt(fixtureParticipant));
                }
                if (fixtureParticipantList) {
                    return playerIds.some((h) => fixtureParticipantList.includes(h));
                }
                if (optionFixtureParticipantList) {
                    return playerIds.some((h) => optionFixtureParticipantList.includes(h));
                }

                return false;
            });
        }
    }

    private filterOptionGroupByTv2MarketParams(
        event: EventModel,
        tv2marketParamvalues: string[] | undefined,
        tvmarketParameterKey: Tv2MarketParameters,
    ): void {
        if (tv2marketParamvalues !== undefined) {
            event.optionGroups = event.optionGroups.filter((og) => {
                const tv2marketParam = og.parameters?.find((p) => p.key === tvmarketParameterKey)?.value;

                return tv2marketParam !== undefined && tv2marketParamvalues.includes(tv2marketParam);
            });
        }
    }

    filterEventWithTemplates(event: EventModel, pod: TopEventsPodData): EventModel {
        const marketParameters = pod.marketParameters;
        const matchingParameters = this.getMatchedParameters(event, pod, marketParameters);

        if (!matchingParameters?.length) {
            return event;
        }

        //filtering splitFixture option groups if marketFixtureSource is Split
        if (event.hybridFixtureData && pod.splitFixtures?.length && pod.marketFixtureSource === TopEventMarketFixtureSource.Split) {
            const gameIds = new Set(pod.splitFixtures?.flatMap((fixture) => fixture.games.map((game) => game.id)));
            event.optionGroups = event.optionGroups.filter((optionGroup) => gameIds.has(Number(optionGroup.id)));
        }

        const templateIds = this.getValuesForTopEventMarketParameters(matchingParameters, (t) => t.templateIds);
        const filteredV1Optiongroups = this.filterOptionGroupsByTemplateIds(event, templateIds);

        const playerIds = this.getValuesForTopEventMarketParameters(matchingParameters, (p) => p.playerIds);
        this.filterOptionGroupsByPlayerIds(event, playerIds, pod);
        this.filterOptionGroupsByHybridPlayerIds(event, playerIds, pod);

        entries(this.tv2MarketParamsDict).forEach(([key, value]) => {
            this.filterOptionGroupByTv2MarketParams(event, this.getParameterValuesByKey(matchingParameters, key), value);
        });

        if (playerIds || matchingParameters.some((mp) => mp.parameters?.length)) {
            event.optionGroups = [...filteredV1Optiongroups, ...event.optionGroups];
        } else {
            event.optionGroups = [...filteredV1Optiongroups];
        }

        return event;
    }

    private getMatchedParameters(
        event: EventModel,
        pod: TopEventsPodData,
        marketParameters: TopEventsMarketParameters[] | undefined,
    ): TopEventsMarketParameters[] | undefined {
        if (!marketParameters) return undefined;

        const hybridFixtureId = event.hybridFixtureData?.fixtureId;
        const { id, splitFixtureIds } = event;

        switch (pod.marketFixtureSource) {
            case TopEventMarketFixtureSource.All:
                return marketParameters.filter(
                    (p) => p.fixtureId === hybridFixtureId || p.fixtureId === id || splitFixtureIds?.includes(p.fixtureId),
                );
            case TopEventMarketFixtureSource.Split:
                return marketParameters.filter((p) => splitFixtureIds?.includes(p.fixtureId));
            default:
                return marketParameters.filter((p) => p.fixtureId === hybridFixtureId || p.fixtureId === id);
        }
    }

    private getValuesForTopEventMarketParameters(
        matchingParameters: TopEventsMarketParameters[],
        keySelector: (p: TopEventsMarketParameters) => number[] | undefined,
    ) {
        const values = matchingParameters.flatMap(keySelector).filter(isDefined);

        return values.length ? values : undefined;
    }

    private getParameterValuesByKey(parameters: TopEventsMarketParameters[], key: string): string[] | undefined {
        const filteredParameters = parameters
            .flatMap((p) => p.parameters)
            .filter((param) => param?.key === key)
            .map((param) => param!.value);

        return filteredParameters.length > 0 ? filteredParameters : undefined;
    }
}
